import * as cornerstone from 'cornerstone-core';
import * as cornerstoneWADOImageLoader from 'cornerstone-wado-image-loader';
import dicomParser from 'dicom-parser';
import db from '../database/db';

// ===========================================================

// TEMP: DISABLE SOME WARNING IN CONSOLE LOGS FOR EASY DEBUGGING

cornerstoneWADOImageLoader.webWorkerManager.terminate();
cornerstoneWADOImageLoader.configure({
  useWebWorkers: false,
});

//===========================================================

/**
 * Check if a file is a DICOM file by reading its header
 * DICOM files have 'DICM' at bytes 128-131
 */
export const isDicomFile = async (file) => {
  return new Promise((resolve) => {
    const reader = new FileReader();
    
    reader.onload = (e) => {
      try {
        const arrayBuffer = e.target.result;
        const byteArray = new Uint8Array(arrayBuffer);
        
        // Check for DICOM magic number 'DICM' at offset 128
        if (byteArray.length >= 132) {
          const dicm = String.fromCharCode(
            byteArray[128],
            byteArray[129],
            byteArray[130],
            byteArray[131]
          );
          
          if (dicm === 'DICM') {
            resolve(true);
            return;
          }
        }
        
        // Try parsing as DICOM (some files may not have the preamble)
        try {
          dicomParser.parseDicom(byteArray);
          resolve(true);
          return;
        } catch (e) {
          // Not a valid DICOM file
        }
        
        resolve(false);
      } catch (error) {
        resolve(false);
      }
    };
    
    reader.onerror = () => resolve(false);
    
    // Read first 1KB to check header
    reader.readAsArrayBuffer(file.slice(0, 1024));
  });
};

/**
 * Load DICOM file and extract metadata
 */
export const loadDicomFile = async (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = async (e) => {
      try {
        const arrayBuffer = e.target.result;
        
        // Parse DICOM file
        const byteArray = new Uint8Array(arrayBuffer);
        const dataSet = dicomParser.parseDicom(byteArray);

        // Extract metadata
        const metadata = extractMetadata(dataSet);
        
        // Create image ID for cornerstone
        const imageId = cornerstoneWADOImageLoader.wadouri.fileManager.add(file);
        
        // Store in IndexedDB
        await storeFileData(file, metadata, imageId, arrayBuffer);

        resolve({ imageId, metadata });
      } catch (error) {
        reject(error);
      }
    };

    reader.onerror = reject;
    reader.readAsArrayBuffer(file);
  });
};

/**
 * Extract DICOM metadata
 */
const extractMetadata = (dataSet) => {
  const getString = (tag) => {
    try {
      const element = dataSet.elements[tag];
      return element ? dataSet.string(tag) : '';
    } catch (e) {
      return '';
    }
  };

  const getNumber = (tag) => {
    try {
      const element = dataSet.elements[tag];
      return element ? dataSet.floatString(tag) : null;
    } catch (e) {
      return null;
    }
  };

  return {
    // Patient Information
    patientName: getString('x00100010'),
    patientID: getString('x00100020'),
    patientBirthDate: getString('x00100030'),
    patientSex: getString('x00100040'),

    // Study Information
    studyInstanceUID: getString('x0020000d'),
    studyDate: getString('x00080020'),
    studyTime: getString('x00080030'),
    studyDescription: getString('x00081030'),
    studyID: getString('x00200010'),
    accessionNumber: getString('x00080050'),
    institutionName: getString('x00080080'),

    // Series Information
    seriesInstanceUID: getString('x0020000e'),
    seriesDate: getString('x00080021'),
    seriesTime: getString('x00080031'),
    seriesNumber: getString('x00200011'),
    seriesDescription: getString('x0008103e'),
    modality: getString('x00080060'),

    // Image Information
    sopInstanceUID: getString('x00080018'),
    instanceNumber: getString('x00200013'),
    
    // Image Orientation and Position
    imageOrientationPatient: getString('x00200037'),
    imagePositionPatient: getString('x00200032'),
    sliceLocation: getNumber('x00201041'),
    sliceThickness: getNumber('x00180050'),

    // Image Dimensions
    rows: getNumber('x00280010'),
    columns: getNumber('x00280011'),
    pixelSpacing: getString('x00280030'),

    // Window/Level
    windowCenter: getNumber('x00281050'),
    windowWidth: getNumber('x00281051'),
  };
};

/**
 * Store file data in IndexedDB
 */
const storeFileData = async (file, metadata, imageId, arrayBuffer) => {
  // Determine instance orientation
  const instanceOrientation = determineOrientation(metadata.imageOrientationPatient);
  
  // Store file reference WITH instance orientation
  await db.files.add({
    name: file.name,
    type: file.type,
    sopInstanceUID: metadata.sopInstanceUID,
    seriesInstanceUID: metadata.seriesInstanceUID,
    studyInstanceUID: metadata.studyInstanceUID,
    imageId: imageId,
    orientation: instanceOrientation,
    imageOrientationPatient: metadata.imageOrientationPatient,
  });

  // Store the DICOM arrayBuffer for persistence
  await db.images.add({
    sopInstanceUID: metadata.sopInstanceUID,
    imageData: arrayBuffer,
  });

  // Store patient (use put to avoid duplicates)
  await db.patients.put({
    patientID: metadata.patientID,
    patientName: metadata.patientName,
    patientBirthDate: metadata.patientBirthDate,
    patientSex: metadata.patientSex,
  });

  // Store study (updated to reference patientID only)
  await db.studies.put({
    studyInstanceUID: metadata.studyInstanceUID,
    patientID: metadata.patientID,
    studyDate: metadata.studyDate,
    studyTime: metadata.studyTime,
    studyDescription: metadata.studyDescription,
    studyID: metadata.studyID,
    modality: metadata.modality,
    accessionNumber: metadata.accessionNumber,
    institutionName: metadata.institutionName,
  });

  // Store series with orientation (will be finalized after all instances loaded)
  const orientation = determineOrientation(metadata.imageOrientationPatient);
  await db.series.put({
    seriesInstanceUID: metadata.seriesInstanceUID,
    studyInstanceUID: metadata.studyInstanceUID,
    seriesDate: metadata.seriesDate,
    seriesTime: metadata.seriesTime,
    seriesNumber: metadata.seriesNumber,
    seriesDescription: metadata.seriesDescription,
    modality: metadata.modality,
    orientation: orientation,
  });
};

/**
 * Determine image orientation (Sagittal, Axial, Coronal)
 */
const determineOrientation = (imageOrientationPatient) => {
  if (!imageOrientationPatient) return 'UNKNOWN';

  const values = imageOrientationPatient.split('\\').map(parseFloat);
  if (values.length !== 6) return 'UNKNOWN';

  const rowCosines = [values[0], values[1], values[2]];
  const colCosines = [values[3], values[4], values[5]];

  // Calculate cross product to get slice normal
  const normal = [
    rowCosines[1] * colCosines[2] - rowCosines[2] * colCosines[1],
    rowCosines[2] * colCosines[0] - rowCosines[0] * colCosines[2],
    rowCosines[0] * colCosines[1] - rowCosines[1] * colCosines[0],
  ];

  // Find dominant axis
  const absNormal = normal.map(Math.abs);
  const maxIndex = absNormal.indexOf(Math.max(...absNormal));

  // Determine orientation based on dominant axis
  if (maxIndex === 0) return 'SAGITTAL';  // X-axis (left-right)
  if (maxIndex === 1) return 'CORONAL';   // Y-axis (anterior-posterior)
  if (maxIndex === 2) return 'AXIAL';     // Z-axis (superior-inferior)

  return 'UNKNOWN';
};

/**
 * Finalize orientation for a single series by analyzing all its instances
 * Call this after all DICOM files for a series have been loaded
 * Sets series.orientation to LOCALIZER if multiple planes detected
 */
export const finalizeSeriesOrientation = async (seriesInstanceUID) => {
  try {
    if (!seriesInstanceUID || typeof seriesInstanceUID !== 'string') {
      console.error('Invalid seriesInstanceUID provided to finalizeSeriesOrientation');
      return;
    }
    
    // Get all files (instances) for this series
    const files = await db.files
      .where('seriesInstanceUID')
      .equals(seriesInstanceUID)
      .toArray();
    
    if (files.length === 0) {
      console.warn(`No files found for series: ${seriesInstanceUID.substring(0, 20)}...`);
      return;
    }
    
    // Collect unique planes (ignore UNKNOWN)
    const planesSet = new Set();
    files.forEach(file => {
      if (file.orientation && file.orientation !== 'UNKNOWN') {
        planesSet.add(file.orientation);
      }
    });
    
    const planesArray = Array.from(planesSet);
    
    // Determine final orientation
    let finalOrientation;
    if (planesSet.size >= 2) {
      finalOrientation = 'LOCALIZER';
      console.log(`Series ${seriesInstanceUID.substring(0, 20)}...: LOCALIZER (planes: ${planesArray.join(', ')})`);
    } else if (planesSet.size === 1) {
      finalOrientation = planesArray[0];
      console.log(`Series ${seriesInstanceUID.substring(0, 20)}...: ${finalOrientation}`);
    } else {
      finalOrientation = 'UNKNOWN';
      console.log(`Series ${seriesInstanceUID.substring(0, 20)}...: UNKNOWN orientation`);
    }
    
    // Get existing series record
    const series = await db.series.get(seriesInstanceUID);
    if (!series) {
      console.error(`Series not found in database: ${seriesInstanceUID}`);
      return;
    }
    
    // Update series orientation in database
    await db.series.put({
      ...series,
      orientation: finalOrientation
    });
    
  } catch (error) {
    console.error('Error finalizing series orientation:', error);
  }
};

/**
 * Load image stack for a series
 */
export const loadSeriesImageStack = async (seriesInstanceUID) => {
  // Get all files for this series
  const files = await db.files
    .where('seriesInstanceUID')
    .equals(seriesInstanceUID)
    .toArray();

  console.log(`Loading ${files.length} images for series`);
  
  const imagePromises = files.map(async (file) => {
    try {
      // ✅ FIX: Use .where() instead of .get() since sopInstanceUID is not the primary key
      const imageRecord = await db.images
        .where('sopInstanceUID')
        .equals(file.sopInstanceUID)
        .first();
      
      if (!imageRecord || !imageRecord.imageData) {
        console.error(`Image data not found for: ${file.sopInstanceUID}`);
        throw new Error(`Image data not found for ${file.sopInstanceUID}`);
      }

      // Re-register the arrayBuffer with fileManager
      const newImageId = cornerstoneWADOImageLoader.wadouri.fileManager.addBuffer(
        imageRecord.imageData
      );

      // Load the image using the new imageId
      const image = await cornerstone.loadImage(newImageId);
      
      return {
        imageId: newImageId,
        image: image,
        instanceNumber: parseInt(image.data.string('x00200013') || '0'),
      };
    } catch (error) {
      console.error(`Error loading image ${file.sopInstanceUID}:`, error);
      throw error;
    }
  });

  const images = await Promise.all(imagePromises);

  // Sort by instance number
  images.sort((a, b) => a.instanceNumber - b.instanceNumber);

  return images.map(img => img.imageId);
};

/**
 * Clear all DICOM data from IndexedDB
 */
export const clearAllDicomData = async () => {
  try {
    await db.files.clear();
    await db.series.clear();
    await db.studies.clear();
    await db.patients.clear();
    await db.images.clear();
    console.log('All DICOM data cleared from IndexedDB');
  } catch (error) {
    console.error('Error clearing DICOM data:', error);
    throw error;
  }
};